import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { MapPin, Calendar, Package } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatApiErrorDetail } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import MobileNav from '../components/MobileNav';
import BrandLogo from '../components/BrandLogo';
import Lightbox from '../components/Lightbox';
import StarRating from '../components/StarRating';
import ReportButton from '../components/ReportButton';
import ShareButtons from '../components/ShareButtons';
import VendorGmapsReviews from '../components/VendorGmapsReviews';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VendorDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [vendor, setVendor] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [reviewSummary, setReviewSummary] = useState({ count: 0, average: 0 });
  const [bookingData, setBookingData] = useState({
    event_type: 'wedding',
    event_date: '',
    notes: '',
    total_price: 0,
  });

  useEffect(() => {
    fetchVendorDetails();
  }, [id]);

  const fetchVendorDetails = async () => {
    try {
      const [vendorRes, servicesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/vendors/${id}`),
        axios.get(`${BACKEND_URL}/api/services/vendor/${id}`),
      ]);
      setVendor(vendorRes.data);
      setServices(servicesRes.data);
      // Fetch gallery (public endpoint — fails silently for non-photographers)
      try {
        const galleryRes = await axios.get(`${BACKEND_URL}/api/vendor/${id}/gallery`);
        setGallery(galleryRes.data.images || []);
      } catch {}
      // Fetch reviews summary
      try {
        const rRes = await axios.get(`${BACKEND_URL}/api/reviews?target_id=${id}`);
        setReviewSummary({ count: rRes.data?.count || 0, average: rRes.data?.average || 0 });
      } catch {}
    } catch (error) {
      toast.error('Error loading vendor details');
    } finally {
      setLoading(false);
    }
  };

  const handleBookService = (service) => {
    if (!user || user.role !== 'customer') {
      toast.error('Please login as a customer to book services');
      navigate('/login');
      return;
    }
    setSelectedService(service);
    setBookingData({ ...bookingData, total_price: service.base_price });
    setShowBookingForm(true);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (!selectedService) return;

    try {
      await axios.post(
        `${BACKEND_URL}/api/bookings`,
        {
          vendor_id: id,
          service_id: selectedService.id,
          event_type: bookingData.event_type,
          event_date: bookingData.event_date,
          total_price: bookingData.total_price,
          notes: bookingData.notes,
        },
        { withCredentials: true }
      );
      toast.success('Booking request submitted successfully!');
      setShowBookingForm(false);
      setSelectedService(null);
      navigate('/dashboard/customer');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand-50">
        <div className="font-heading text-2xl">Loading...</div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sand-50">
        <div className="text-center">
          <h2 className="font-heading text-3xl mb-4">Vendor not found</h2>
          <Link to="/" className="text-brand-primary hover:underline">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      {/* SEO — LocalBusiness + canonical for this vendor. */}
      <Helmet>
        <title>{`${vendor.business_name || vendor.name} ${vendor.city ? '— ' + vendor.city : ''} | BeeWeddy`}</title>
        <meta name="description" content={
          `${vendor.business_name || vendor.name}${vendor.city ? ' in ' + vendor.city : ''} — ` +
          `${(vendor.bio || 'verified event vendor').slice(0, 150)}. Get instant pricing on BeeWeddy.`
        } />
        <link rel="canonical" href={`${BACKEND_URL}/vendors/${vendor.id || vendor._id}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${BACKEND_URL}/vendors/${vendor.id || vendor._id}`,
            "name": vendor.business_name || vendor.name,
            "image": (vendor.gallery_images || []).slice(0, 4).map(
              (g) => (g && g.startsWith('http') ? g : `${BACKEND_URL}${g || ''}`)
            ),
            "url": `${BACKEND_URL}/vendors/${vendor.id || vendor._id}`,
            "description": vendor.bio || undefined,
            "address": vendor.city ? {
              "@type": "PostalAddress",
              "addressLocality": vendor.city,
              "addressCountry": "IN",
            } : undefined,
            "areaServed": vendor.city || undefined,
            "category": vendor.category || vendor.role,
            "aggregateRating": reviewSummary.count > 0 ? {
              "@type": "AggregateRating",
              "ratingValue": Number(reviewSummary.average).toFixed(1),
              "reviewCount": Number(reviewSummary.count),
              "bestRating": "5",
            } : undefined,
          })}
        </script>
      </Helmet>
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 bg-sand-50/80 border-b border-[#E8E1D5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-5 flex justify-between items-center">
          <BrandLogo size="md" linkTo="/" />
          <MobileNav
            links={[
              { to: '/instant-pricing', label: t('home.get_instant_pricing'), className: 'text-sm tracking-[0.2em] uppercase font-bold text-brand-primary hover:text-[#A84A38] transition-colors', testId: 'header-pricing-link' },
            ]}
            actions={user ? [
              { to: '/dashboard', label: t('common.dashboard') || 'Dashboard', className: 'text-sm tracking-[0.2em] uppercase font-bold text-text-secondary hover:text-text-primary transition-colors', testId: 'header-dashboard-link' },
              { onClick: logout, label: t('common.logout'), className: 'text-sm tracking-[0.2em] uppercase font-bold text-text-secondary hover:text-text-primary transition-colors', testId: 'header-logout-btn' },
            ] : [
              { to: '/login', label: t('common.login'), className: 'bg-brand-primary text-sand-50 rounded-none px-6 py-3 font-medium hover:bg-[#A84A38] transition-colors', testId: 'header-login-link' },
            ]}
          />
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        {/* Vendor Header */}
        <div className="mb-12">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-3">{vendor.category}</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-none text-text-primary mb-4">
            {vendor.business_name || vendor.name}
          </h1>
          {vendor.location && (
            <div className="flex items-center gap-2 text-text-secondary mb-4">
              <MapPin size={20} weight="duotone" />
              <span>{vendor.location}</span>
            </div>
          )}
          {reviewSummary.count > 0 && (
            <div className="flex items-center gap-2 mb-4" data-testid="vendor-review-summary">
              <StarRating value={Math.round(reviewSummary.average)} readonly size={20} testIdPrefix="vendor-rating" />
              <span className="text-sm font-bold text-text-primary">{reviewSummary.average}</span>
              <span className="text-sm text-text-secondary">({reviewSummary.count} {reviewSummary.count === 1 ? 'review' : 'reviews'})</span>
            </div>
          )}
          <div className="mb-4">
            <ReportButton
              targetType="vendor"
              targetId={vendor.id}
              targetName={vendor.business_name || vendor.name}
              variant="text"
              testId="vendor-report-btn"
            />
          </div>
          {/* Share this vendor */}
          <div className="mb-6">
            <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">Share this vendor</div>
            <ShareButtons
              text={`${vendor.business_name || vendor.name}${vendor.city ? ' in ' + vendor.city : ''} on BeeWeddy`}
              hashtags="BeeWeddy,WeddingVendors"
            />
          </div>
          {vendor.description && <p className="text-base leading-relaxed text-text-secondary max-w-3xl">{vendor.description}</p>}
        </div>

        {/* Google Reviews — shows only if vendor has imported any. */}
        <VendorGmapsReviews vendorId={id} />

        {/* Gallery (photographer & any vendor with uploaded work) */}
        {gallery.length > 0 && (
          <div className="mb-12" data-testid="vendor-gallery-section">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium mb-6">Work Gallery</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {gallery.map((src, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setLightboxIdx(idx)}
                  className="relative aspect-square border border-[#E8E1D5] overflow-hidden group cursor-zoom-in"
                  data-testid={`vendor-gallery-img-${idx}`}
                >
                  <img src={src} alt={`Work ${idx + 1}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Services */}
        <div className="mb-12">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium mb-8">Services & Pricing</h2>
          {services.length === 0 ? (
            <p className="text-text-secondary">No services available at the moment.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service) => (
                <Card key={service.id} className="bg-white border-[#E8E1D5]" data-testid={`service-card-${service.id}`}>
                  <CardHeader>
                    <CardTitle className="font-heading text-2xl">{service.name}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">Base Price</div>
                      <div className="font-heading text-3xl text-brand-primary" data-testid={`service-price-${service.id}`}>
                        ₹{service.base_price}
                      </div>
                    </div>
                    {service.packages && service.packages.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-3">Packages</div>
                        <div className="space-y-2">
                          {service.packages.map((pkg, idx) => (
                            <div key={idx} className="border border-[#E8E1D5] p-3">
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-medium">{pkg.name}</span>
                                <span className="font-heading text-xl text-brand-primary">₹{pkg.price}</span>
                              </div>
                              {pkg.features && (
                                <ul className="text-sm text-text-secondary space-y-1">
                                  {pkg.features.map((feature, fIdx) => (
                                    <li key={fIdx}>• {feature}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <Button
                      onClick={() => handleBookService(service)}
                      className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                      data-testid={`book-service-btn-${service.id}`}
                    >
                      Book This Service
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Booking Form Modal */}
        {showBookingForm && selectedService && (
          <div className="fixed inset-0 bg-text-primary/50 flex items-center justify-center z-50 p-8">
            <Card className="w-full max-w-2xl bg-sand-50 max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="font-heading text-3xl">Book {selectedService.name}</CardTitle>
                <CardDescription>Fill in the details for your booking</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitBooking} className="space-y-6">
                  <div>
                    <Label htmlFor="event_type" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                      Event Type
                    </Label>
                    <Select value={bookingData.event_type} onValueChange={(value) => setBookingData({ ...bookingData, event_type: value })}>
                      <SelectTrigger className="rounded-none border-[#E8E1D5]" data-testid="booking-event-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wedding">Wedding</SelectItem>
                        <SelectItem value="birthday">Birthday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="event_date" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                      Event Date
                    </Label>
                    <Input
                      id="event_date"
                      type="date"
                      value={bookingData.event_date}
                      onChange={(e) => setBookingData({ ...bookingData, event_date: e.target.value })}
                      required
                      className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                      data-testid="booking-event-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="total_price" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                      Total Price
                    </Label>
                    <Input
                      id="total_price"
                      type="number"
                      value={bookingData.total_price}
                      onChange={(e) => setBookingData({ ...bookingData, total_price: parseFloat(e.target.value) })}
                      required
                      className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                      data-testid="booking-total-price"
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes" className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary">
                      Notes (Optional)
                    </Label>
                    <Textarea
                      id="notes"
                      value={bookingData.notes}
                      onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                      rows={4}
                      className="rounded-none border-[#E8E1D5] focus:ring-brand-primary"
                      data-testid="booking-notes"
                    />
                  </div>
                  <div className="flex gap-4">
                    <Button type="submit" className="flex-1 bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="submit-booking-btn">
                      Submit Booking
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setShowBookingForm(false);
                        setSelectedService(null);
                      }}
                      className="flex-1 bg-transparent border border-text-primary text-text-primary hover:bg-text-primary hover:text-sand-50 rounded-none"
                      data-testid="cancel-booking-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Gallery Lightbox */}
      <Lightbox
        open={lightboxIdx !== null}
        photos={gallery}
        initialIndex={lightboxIdx || 0}
        title={vendor.business_name || vendor.name || 'Work Gallery'}
        onClose={() => setLightboxIdx(null)}
      />
    </div>
  );
};

export default VendorDetailPage;
